import React from 'react';
import {useSelector} from 'react-redux';
import moment from 'moment';
import {goalPerRateEnumList, Images} from 'utils';
import {DRIVE, SERVICE, STAFF, UNASSIGNED_STATUS, UNAVAILABLE} from '../constants';
import {apptEnums, scheduleStatusEnums, scheduleTypes} from './constants';
import {SelectedFilters, SimpleTooltip} from 'components';
import {useLocation, useNavigate} from 'react-router-dom';
import {cptServiceLabel, renderClientName, renderStaffName} from 'utils';

export const ScheduleInformation = ({fromCalendar}) => {
    const {appointments} = useSelector((state) => ({
        appointments: state.appointment.appointments,
    }));
    const globalServices = useSelector((state) => state.fundingSource.fundingUniqueServices);
    const notCancelledAppts = appointments?.appts?.filter((i) => i?.status !== apptEnums.CANCELLED && i?.type !== UNAVAILABLE);
    const driveAppt = notCancelledAppts?.filter((i) => i?.type === DRIVE);
    // const allCompleted = notCancelledAppts?.filter((i) => i?.status !== apptEnums.CANCELLED);
    const worked = notCancelledAppts?.filter((i) => i?.type === SERVICE);
    const staffAppt = notCancelledAppts?.filter((i) => i?.type === STAFF);
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;

    const calculateTotalTime = (list) => {
        let totalMinutes = 0;
        list?.forEach((event) => {
            if (event.startTime && event.endTime) {
                const start = moment.utc(event.startTime);
                const end = moment.utc(event.endTime);
                const diff = end.diff(start, 'minutes');
                totalMinutes += diff;
            }
        });
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}h ${minutes}m`;
    };

    const calculateMileage = (list) => {
        let totalMiles = 0;
        list?.forEach((event) => {
            totalMiles += Number(event?.miles) || 0;
        });
        return `${totalMiles.toFixed(0)}mi`;
    };

    /**
     * Every filter that narrows the appointment list, declared once.
     *
     * Three sources feed it: the filter panel (`core/filters.js`), the table column headers
     * (`apptHead` → Type / Service / Status) and the totals cards below. The column filters write
     * plain id arrays onto `location.state` exactly like the panel does, so leaving them out meant a
     * grid could be silently filtered — and every total on this card silently wrong — with nothing
     * on screen to say so.
     *
     * `staffFull` / `clientFull` hold the selected objects and `staffs` / `clients` the matching
     * ids; `pairKey` keeps the two in step when one chip is removed.
     */
    const filtersList = [
        {name: 'Staff', key: 'staffFull', pairKey: 'staffs', render: (s) => renderStaffName(s)},
        {name: 'Client', key: 'clientFull', pairKey: 'clients', render: (c) => renderClientName(c)},
        {name: 'Payer', key: 'payerFull', companions: ['payer'], render: (p) => p?.name},
        {name: 'Errored', key: 'errors', render: (e) => e?.name ?? e},
        {label: 'w/o Notes', key: 'withoutNotes'},
        {label: 'w/o Signature', key: 'withoutSignature'},
        {label: 'w/o Client Signature', key: 'withoutClientSignature'},
        {label: 'w/o Provider Signature', key: 'withoutProviderSignature'},
        {label: 'Supervision', key: 'supervision'},
        // Table column-header filters. Titles match the columns they came from (`apptHead`), so a
        // chip points back at the control that set it.
        //
        // The Service / Drive / Staff cards below set `types` + `statuses` + `selectedFilter` as one
        // unit, and a card's highlighted border keys off `selectedFilter` alone. Editing either
        // array from a chip breaks that pairing, so the highlight goes with it — otherwise a card
        // keeps claiming a filter the user just removed.
        {name: 'Type', key: 'types', options: scheduleTypes, companions: ['selectedFilter']},
        {name: 'Service', key: 'cptCode', render: (c) => cptServiceLabel(globalServices, c?.id ?? c)},
        {name: 'Status', key: 'statuses', options: scheduleStatusEnums, companions: ['selectedFilter']},
        // Declared only so "Clear Filters" clears it. It is set by the billable-appointments toggle,
        // which shows its own state, so it never earns a chip of its own.
        {key: 'authBillable', show: false},
    ];

    const list = [
        {
            icon: Images.worked,
            title: 'Service',
            value: calculateTotalTime(worked),
            background: '#E6F5EE',
            color: '#026C3C',
            text: 'Total worked hours',
            activeBorderColor: info?.selectedFilter === 'service' ? '#8BD0B1' : '',
            handleClick: () => {
                const reqParams = {...info}
                if (reqParams?.selectedFilter === 'service') {
                    delete reqParams.selectedFilter;
                    delete reqParams.types;
                    delete reqParams.statuses;
                } else {
                    reqParams.types = [SERVICE];
                    reqParams.statuses = [apptEnums.COMPLETED, apptEnums.PENDING, UNASSIGNED_STATUS];
                    reqParams.selectedFilter = 'service';
                }
                navigate('/schedule', {state: reqParams})
            },
        },
        {
            icon: Images.truck,
            title: 'Drive Time',
            value: calculateTotalTime(driveAppt),
            background: '#FEFADC',
            color: '#313131',
            text: 'Total driving time',
            activeBorderColor: info?.selectedFilter === 'drive' ? '#F8EA7F' : '',
            handleClick: () => {
                const reqParams = {...info}
                if (reqParams?.selectedFilter === 'drive') {
                    delete reqParams.selectedFilter;
                    delete reqParams.types;
                    delete reqParams.statuses;
                } else {
                    reqParams.types = [DRIVE];
                    reqParams.statuses = [apptEnums.COMPLETED, apptEnums.PENDING];
                    reqParams.selectedFilter = 'drive';
                }
                navigate('/schedule', {state: reqParams})
            },
        },
        {
            icon: Images.apptsCalendar,
            title: 'Staff',
            value: staffAppt?.length || 0,
            background: '#EFF8FF',
            color: '#175CD3',
            text: 'Total scheduled appointments',
            activeBorderColor: info?.selectedFilter === 'staff' ? '#8FB9F2' : '',
            handleClick: () => {
                const reqParams = {...info}

                if (reqParams?.selectedFilter === 'staff') {
                    delete reqParams.selectedFilter;
                    delete reqParams.types;
                    delete reqParams.statuses;
                } else {
                    reqParams.types = [STAFF];
                    reqParams.statuses = [apptEnums.COMPLETED, apptEnums.PENDING];
                    reqParams.selectedFilter = 'staff';
                }
                navigate('/schedule', {state: reqParams})
            },
        },
        {
            icon: Images.map,
            title: 'Mileage',
            value: calculateMileage(driveAppt),
            background: '#F8F9FC',
            color: '#363F72',
            text: 'Total mileage for drive time appointments',
        },
        // {
        //     icon: Images.apptsCalendar,
        //     title: 'Appts',
        //     value: notCancelledAppts?.length || 0,
        //     background: '#EFF8FF',
        //     color: '#175CD3',
        //     text: 'Total scheduled appointments',
        // },
        {
            icon: Images.clock,
            title: 'Total',
            value: calculateTotalTime(notCancelledAppts),
            background: '#EFF8FF',
            color: '#175CD3',
            isLast: true,
            text: 'Combined total of worked and drive time',
        },
    ];

    return (
        <div>
            <div style={{background: '#F8FAFB', padding: fromCalendar ? '0 16px 1px' : 0}}>
            <SelectedFilters filtersList={filtersList} doNotCrop />
            </div>

            <div className="schedule-information-wrapper space-between">
                <div className="schedule-information-box">
                    {list?.map((i, j) => (
                        <div key={j} className="flex-align-center" style={{gap: 8}}>
                            <SimpleTooltip title={i?.text} placement="top" arrow>
                                <div
                                    onClick={i?.handleClick}
                                    className="schedule-information-card"
                                    style={{
                                        background: i?.background,
                                        borderColor: i?.activeBorderColor ? i?.activeBorderColor : '#E4E7EC',
                                        borderWidth: i?.activeBorderColor ? 2 : 1,
                                        borderStyle: 'solid',
                                        cursor: i?.handleClick ? 'pointer' : 'default'
                                    }}
                                >
                                    <img src={i?.icon} alt="icon" style={{width: 14, height: 14}}/>
                                    <p style={{
                                        color: i?.color,
                                        whiteSpace: 'nowrap',
                                        fontSize: 13,
                                        margin: 0,
                                        padding: 0
                                    }}>{i?.title}:</p>
                                    <span style={{
                                        color: i?.color,
                                        whiteSpace: 'nowrap',
                                        fontSize: 13,
                                        fontWeight: 600
                                    }}>{i?.value}</span>
                                </div>
                            </SimpleTooltip>
                            {!i?.isLast &&
                                <div className="info-line" style={{height: 16, width: 1, background: '#E4E7EC'}}/>
                            }
                        </div>
                    ))}
                </div>
            </div>

        </div>
    );
};
